import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useUser } from "../../user_context";
import './css/emp_layout.css';

const fontLink = document.createElement("link");
fontLink.rel = "stylesheet";
fontLink.href = "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;1,400&family=DM+Sans:wght@300;400;500&display=swap";
if (!document.querySelector("link[href*='Cormorant']")) document.head.appendChild(fontLink);

export default function EmpLayout({ children, title, titleEm, topbarRight }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const { userData, loading } = useUser();

  const active = (path) => location.pathname === path;

  // 1. Sidebar Nav Items: Added explicit search index menu row
  // const NAV_ITEMS = [
  //   { path: "/employer/dashboard", icon: "◈", label: "Dashboard" },
  //   { path: "/employer/candidate-search", icon: "🔍", label: "Find Talent" },
  //   { path: "/employer/post-job", icon: "+", label: "Post a Job" },
  //   { path: "/employer/listings", icon: "✦", label: "My Listings", badge: userData?.listingsCount > 0 ? userData.listingsCount : null },
  //   { path: "/employer/profile", icon: "◯", label: "Business Profile", id: "profile" },
  //   { path: "/employer/marketplace", icon: "◯", label: "Market Place", id: "marketplace" },
  // ];

  const NAV_ITEMS = [
  { 
    path: "/employer/dashboard", 
    label: "Dashboard",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="9" />
        <rect x="14" y="3" width="7" height="5" />
        <rect x="14" y="12" width="7" height="9" />
        <rect x="3" y="16" width="7" height="5" />
      </svg>
    )
  },
  { 
    path: "/employer/candidate-search", 
    label: "Find Talent",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
        <path d="M11 7a4 4 0 0 0-4 4" />
      </svg>
    )
  },
  { 
    path: "/employer/post-job", 
    label: "Post a Job",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <line x1="12" y1="8" x2="12" y2="16" />
        <line x1="8" y1="12" x2="16" y2="12" />
      </svg>
    )
  },
  { 
    path: "/employer/listings", 
    label: "My Listings", 
    badge: userData?.listingsCount > 0 ? userData.listingsCount : null,
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <polyline points="9 11 11 13 15 9" />
      </svg>
    )
  },
  { 
    path: "/employer/profile", 
    label: "Business Profile", 
    id: "profile",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
      </svg>
    )
  },
  { 
    path: "/employer/marketplace", 
    label: "Market Place", 
    id: "marketplace",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
        <line x1="3" y1="6" x2="21" y2="6" />
        <path d="M16 10a4 4 0 0 1-8 0" />
      </svg>
    )
  }
];

  // 2. Mobile Bottom Bar Nav Items: Integrated search action hub
  const BOTTOM_NAV = [
    { path: "/employer/dashboard", icon: "◈", label: "Home" },
    { path: "/employer/candidate-search", icon: "🔍", label: "Search" },
    { path: "/employer/post-job", icon: "+", label: "Post a Job" },
    { path: "/employer/listings", icon: "✦", label: "Listings" },
  ];

  useEffect(() => {
    if (!loading && !userData) {
      navigate("/signin", { replace: true });
    }
    const token = localStorage.getItem("token");
    if (!token && userData) {
      window.location.href = "/signin";
    }
  }, [userData, loading, navigate]);

  if (loading) {
    return (
      <div className="layout-loading-screen">
        <div className="spinner">Loading your profile...</div>
      </div>
    );
  }

  if (!userData) return null;

  const displayName = userData?.displayName || "Employer";
  const initials = userData?.initials || "--";

  return (
    <div className="emp-root">
      <div className={`emp-overlay${open ? " open" : ""}`} onClick={() => setOpen(false)} />

      {/* Sidebar Panel Configuration */}
      <aside className={`emp-sidebar${open ? " open" : ""}`}>
        <Link to="/" className="emp-logo">Rembo<span>List</span></Link>

        <div className="emp-nav-label">Main</div>
        {NAV_ITEMS.map(n => (
          <Link key={n.path} to={n.path} className={`emp-nav-item${active(n.path) ? " active" : ""}`} onClick={() => setOpen(false)}>
            <span className="emp-nav-icon" style={{ fontSize: n.icon === "🔍" ? "0.85rem" : "inherit" }}>{n.icon}</span>
            {n.label}
            {n.badge && <span className="emp-nav-badge">{n.badge}</span>}
          </Link>
        ))}

        {userData?.isAdmin && (
          <>
            <div className="emp-nav-label">Admin</div>
            <Link
              to="/admin/metrics"
              className={`emp-nav-item${active("/admin/metrics") ? " active" : ""}`}
              onClick={() => setOpen(false)}
            >
              <span className="emp-nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2l8 4v6c0 5-3.4 8.4-8 10-4.6-1.6-8-5-8-10V6z" />
                  <path d="M9 12l2 2 4-4" />
                </svg>
              </span>
              Administration
            </Link>
          </>
        )}

        <div className="emp-sidebar-footer">
          <div className="emp-user-chip">
            <div className="emp-avatar">{loading ? ".." : initials}</div>
            <div>
              <div className="emp-user-name">{loading ? "Loading..." : displayName}</div>
              <div className="emp-user-role">Employer account</div>
            </div>
          </div>
          <button className="emp-signout" onClick={() => {
            localStorage.clear();
            navigate("/signin");
          }}>↩ Sign out</button>
        </div>
      </aside>

      {/* Main Top Header Controls layout */}
      <header className="emp-topbar">
        <div className="emp-topbar-left">
          <button className="emp-hamburger" onClick={() => setOpen(true)} aria-label="Menu">☰</button>
          <div className="emp-topbar-title">{title}{titleEm && <> — <em>{titleEm}</em></>}</div>
        </div>
        
        <div className="emp-topbar-right">
          {topbarRight}
          
          {/* 3. Topbar Search Action Button Trigger */}
          <button 
            type="button"
            className={`emp-notif-btn ${active("/employer/candidate-search") ? "active-search" : ""}`}
            onClick={() => navigate("/employer/candidate-search")}
            title="Search Candidate Directory"
            style={{ fontSize: "1rem", marginRight: "0.25rem" }}
          >
            🔍
          </button>

          <Link to="/employer/post-job" className="emp-post-btn">+ Post a Job</Link>
          
          {/* <button className="emp-notif-btn">
            🔔
            {userData?.notifications > 0 && (
              <span className="emp-notif-dot">{userData.notifications}</span>
            )}
          </button> */}
          <div className="emp-avatar">{initials}</div>
        </div>
      </header>

      {/* Main App Content View Frame */}
      <main className="emp-main">{children}</main>

      {/* Footer Mobile Drawer Systems */}
      <nav className="emp-bottom-nav">
        <div className="emp-bottom-nav-inner">
          {BOTTOM_NAV.map(n => (
            <Link key={n.path} to={n.path} className={`emp-bn-item${active(n.path) ? " active" : ""}`}>
              <span className="emp-bn-icon">{n.icon}</span>
              <span className="emp-bn-label">{n.label}</span>
            </Link>
          ))}
        </div>
      </nav>
    </div>
  );
}